import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { ProducerStatus } from '@core/models/producer';
import { ProducerIdentityService } from '@core/services/producer-identity.service';
import { ProducerService } from '@core/services/producer.service';

// Login-first onboarding routes by Producer.Status (MLP-1517): a producer that has completed onboarding — Ready
// (submitted, awaiting admin approval) or Active (approved) — belongs in the app; anyone earlier (none created yet,
// Lead, or Pending/in-progress) belongs in onboarding. Ready producers reach the app before approval for now; the
// pre-approval in-app experience is still to be defined. Both guards first run the shared sign-in→ready sequence
// (facet bootstrap + re-mint, then load the producer record) so the status read is settled.

// A producer that has submitted onboarding, so belongs in the app rather than onboarding.
const hasCompletedOnboarding = (status?: ProducerStatus): boolean =>
  status === ProducerStatus.Ready || status === ProducerStatus.Active;

/** Gate the app routes: only an onboarded producer may enter; anyone else is sent to onboarding. */
export const producerOnboardedGuard: CanActivateFn = async () => {
  const identity = inject(ProducerIdentityService);
  const producerService = inject(ProducerService);
  const router = inject(Router);

  await identity.ensureProducerReady();
  return hasCompletedOnboarding(producerService.producer()?.status) ? true : router.createUrlTree(['/onboarding']);
};

/** Gate the onboarding route: an onboarded producer is sent to the app; anyone else stays to onboard (or resume). */
export const onboardingGuard: CanActivateFn = async () => {
  const identity = inject(ProducerIdentityService);
  const producerService = inject(ProducerService);
  const router = inject(Router);

  await identity.ensureProducerReady();
  return hasCompletedOnboarding(producerService.producer()?.status) ? router.createUrlTree(['/dashboard']) : true;
};
